import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, RotateCcw, FileDown, Loader2, Lock, History, ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ReportingPeriodEditDialog, RpNote } from "@/components/ReportingPeriodField";

// Journal Voucher — single-page module with category selector.
//
// Sprint 2026-Feb.
// Phase 1 (this file) ships full CRUD/approve/reverse/PDF for the EB
// category. Phase 2 will unlock FC + OE. Phase 4+ unlocks OI. The
// backend already discriminates by `voucher_type`, uses a per-type
// numbering series (JVEB / JVFC / JVOE / JVOI) and independent
// permission modules — the UI collapses them into one Journal
// Voucher screen with a mandatory Category tab-strip.
//
// Design notes:
//   • Every JV must satisfy Σ debit == Σ credit (server enforced,
//     UI shows live balance banner while editing).
//   • Lines carry optional project_id (strict access rule enforced
//     server-side; UI just surfaces the picker).
//   • Reversal is a one-click action on Approved JVs; server creates
//     a paired mirror voucher.

// Category catalogue — the single source of truth for the tab-strip
// and form-modal selector. `active=true` means the backend + numbering
// + permissions are wired and the UI can create/edit/reverse for that
// category. When later phases flip OE and OI, just switch `active` to
// true and the tab lights up automatically — no other UI wiring
// required. `series` is the internal voucher-number prefix (surfaced
// on every tab so accountants can spot the doc series at a glance).
const _CATEGORIES = [
  { code: "EB", label: "Employee Benefit Expenses", series: "JVEB",
    module: "journal_voucher_eb",
    hint: "Bonus, gratuity, leave encashment, PF employer share, staff welfare.",
    active: true },
  { code: "FC", label: "Finance Cost", series: "JVFC",
    module: "journal_voucher_fc",
    hint: "Interest expense, bank charges, loan processing fees, forex loss.",
    active: true },
  { code: "OE", label: "Other Expenses", series: "JVOE",
    module: "journal_voucher_oe",
    hint: "Audit fees, insurance, bank charges, legal & professional fees, rent provisions, utility provisions, misc write-offs, rounding differences.",
    active: true },
  { code: "OI", label: "Other Income", series: "JVOI",
    module: "journal_voucher_oi",
    hint: "Revenue Adjustments: Insurance Claim Income, Client Incentives, Rebates, Prior Period Revenue, Miscellaneous Other Income. Feeds Project P&L 'Revenue Adjustments (JV)' — outside SI / GST pipeline.",
    active: true },
];

const _categoryByCode = (code) =>
  _CATEGORIES.find((c) => c.code === code) || _CATEGORIES[0];

const _statusColor = (s) => ({
  Draft: "bg-slate-100 text-slate-700",
  Pending: "bg-amber-100 text-amber-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-rose-100 text-rose-800",
  Superseded: "bg-slate-200 text-slate-500",
}[s] || "bg-slate-100 text-slate-700");

const _emptyLine = () => ({
  ledger_id: "",
  ledger_code: "",
  ledger_name: "",
  ledger_type: "",
  account_label: "",       // populated from ledger_name on save (backward compat only)
  narration: "",
  debit: 0,
  credit: 0,
  project_id: "",
  project_name: "",
  business_vertical_id: "",
  cost_centre_id: "",
});

export default function JournalVouchersPage() {
  const [vtype, setVtype] = useState("EB");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [rpEdit, setRpEdit] = useState(null);   // Phase C-Lite — post-approval Reporting Period dialog
  const [editing, setEditing] = useState(null);
  const [revisingId, setRevisingId] = useState("");       // set on Revise-flow so submit hits /revise
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ledgers, setLedgers] = useState([]);            // JV Ledger Master rows
  // Approval-matrix summary indexed by module → { levels, admin_is_final }
  // so the info card can show "Configured (N levels)" or "Default (Admin)".
  const [matrixByModule, setMatrixByModule] = useState({});
  // Filters + pagination — Sprint 2026-Feb JV Stabilization.
  const [filters, setFilters] = useState({
    status: "", branch_id: "", from_date: "", to_date: "",
    ledger_id: "", created_by: "", approved_by: "", search: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const cat = _categoryByCode(vtype);

  // Derived: human-readable Approval Matrix status for the current tab.
  const matrixStatus = useMemo(() => {
    if (!cat.active) return "—";
    const mx = matrixByModule[cat.module];
    if (!mx) return "Default (Auto-approve)";
    const n = (mx.levels || []).length;
    if (n > 0) return `Configured · ${n} level${n > 1 ? "s" : ""}`;
    return mx.admin_is_final ? "Default (Admin final)" : "Not routed";
  }, [cat, matrixByModule]);

  // Ledgers usable in the currently-selected category tab. All
  // active ledgers whose branch_applicability matches (empty = all).
  const usableLedgers = useMemo(() => {
    const bId = filters.branch_id || "";   // "" = no branch filter → show all
    return (ledgers || []).filter((L) => {
      if (!L.active) return false;
      const apply = L.branch_applicability || [];
      if (!apply.length) return true;
      return !bId || apply.includes(bId);
    });
  }, [ledgers, filters.branch_id]);

  const buildQuery = () => {
    const p = new URLSearchParams();
    p.set("voucher_type", vtype);
    p.set("page", String(page));
    p.set("page_size", String(pageSize));
    for (const [k, v] of Object.entries(filters)) {
      if (v) p.set(k, v);
    }
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/journal-vouchers?${buildQuery()}`);
      setRows(r.data || []);
      const total = Number(r.headers?.["x-total-count"] || 0);
      setTotalRows(total || (r.data || []).length);
    } catch (e) {
      if (!_categoryByCode(vtype).active) {
        setRows([]); setTotalRows(0);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load journal vouchers");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.allSettled([
      api.get("/branches"),
      api.get("/projects-picker"),
      api.get("/approval-matrix"),
      api.get("/jv-ledgers?page_size=500&active=true"),
    ]).then(([b, p, m, l]) => {
      if (b.status === "fulfilled") setBranches(b.value.data || []);
      if (p.status === "fulfilled") setProjects(p.value.data || []);
      if (m.status === "fulfilled") {
        const idx = {};
        (m.value.data || []).forEach((mx) => { if (mx.module) idx[mx.module] = mx; });
        setMatrixByModule(idx);
      }
      if (l.status === "fulfilled") setLedgers(l.value.data?.items || []);
    });
  }, []);

  // Reload the list whenever category / filters / page changes.
  useEffect(() => {
    load();
  }, [vtype, page, pageSize, filters]);

  // When category changes, reset to page 1.
  useEffect(() => { setPage(1); }, [vtype]);

  const onNew = () => {
    if (!cat.active) {
      toast.error(`${cat.label} is not available yet — ${cat.phase || "coming soon"}.`);
      return;
    }
    setRevisingId("");
    setEditing({
      voucher_type: vtype,
      voucher_date: new Date().toISOString().slice(0, 10),
      reporting_period: "",
      branch_id: branches[0]?.id || "",
      branch_name: branches[0]?.branch_name || "",
      narration: "",
      lines: [_emptyLine(), _emptyLine()],
      attachments: [],
    });
    setShowForm(true);
  };

  const onEdit = async (row) => {
    if (row.status === "Approved") {
      toast.error("Approved JVs can't be edited — use Revise to create a new revision.");
      return;
    }
    if (row.status === "Rejected" || row.status === "Superseded") {
      toast.error(`${row.status} JVs can't be edited.`);
      return;
    }
    try {
      const r = await api.get(`/journal-vouchers/${row.id}`);
      setRevisingId("");
      setEditing(r.data);
      setShowForm(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load JV");
    }
  };

  const onRevise = async (row) => {
    if (row.status !== "Approved") {
      toast.error("Only Approved JVs can be revised.");
      return;
    }
    if (!window.confirm(
      `Revise JV ${row.voucher_number}?\n\n` +
      `This will supersede the current voucher and create a new revision ` +
      `(${row.voucher_number}/R${(row.revision_number || 0) + 1}) that must go through approval again.`,
    )) return;
    try {
      const r = await api.get(`/journal-vouchers/${row.id}`);
      // Copy the JV into edit state; server will mint the /R<N+1>
      // voucher_number and stamp revision metadata.
      setRevisingId(row.id);
      setEditing({
        ...r.data,
        id: "",                                    // force new-doc save path
        voucher_number: "",
        status: "Draft",
      });
      setShowForm(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load JV for revision");
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete JV ${row.voucher_number || row.id}?`)) return;
    try {
      await api.delete(`/journal-vouchers/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onReverse = async (row) => {
    if (!window.confirm(
      `Reverse JV ${row.voucher_number || row.id}? A new voucher with debit/credit swapped will be created.`,
    )) return;
    try {
      const r = await api.post(`/journal-vouchers/${row.id}/reverse`);
      toast.success(`Reversal ${r.data.voucher_number || r.data.id} created`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reverse failed");
    }
  };

  const onPdf = async (row) => {
    try {
      const r = await api.get(`/journal-vouchers/${row.id}/voucher-pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed");
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="jv-page">
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">
            Journal Voucher
          </h1>
          <p className="text-sm text-slate-500">
            Non-cash accounting entries. Pick a category below and file a
            balanced voucher (Σ debit = Σ credit).
          </p>
        </div>

        {/* Info card — Current Category, Voucher Series, Approval Matrix Status */}
        <div
          className="hidden md:flex flex-col text-xs bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 min-w-[260px]"
          data-testid="jv-info-card"
        >
          <div className="flex justify-between gap-4 py-0.5">
            <span className="text-slate-500">Current Category</span>
            <span
              className="font-semibold text-slate-800 truncate"
              data-testid="jv-info-category"
            >{cat.label}</span>
          </div>
          <div className="flex justify-between gap-4 py-0.5">
            <span className="text-slate-500">Voucher Series</span>
            <span
              className="font-mono text-slate-800"
              data-testid="jv-info-series"
            >{cat.series}</span>
          </div>
          <div className="flex justify-between gap-4 py-0.5">
            <span className="text-slate-500">Approval Matrix</span>
            <span
              className={`font-medium ${
                matrixStatus.startsWith("Configured")
                  ? "text-emerald-700"
                  : "text-amber-700"
              }`}
              data-testid="jv-info-matrix"
            >{matrixStatus}</span>
          </div>
        </div>

        <Button
          onClick={onNew}
          disabled={!cat.active}
          className="bg-orange-600 hover:bg-orange-700 text-white disabled:bg-slate-300"
          data-testid="jv-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Journal Voucher
        </Button>
      </div>

      {/* Category tab-strip — single source of truth for voucher_type. */}
      <div
        className="flex flex-wrap gap-2 border-b border-slate-200 pb-2"
        data-testid="jv-category-tabs"
        role="tablist"
      >
        {_CATEGORIES.map((c) => {
          const selected = c.code === vtype;
          const seriesLabel = c.active
            ? c.series
            : (c.code === "OI" ? `${c.series} – Future` : c.series);
          return (
            <button
              key={c.code}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setVtype(c.code)}
              data-testid={`jv-tab-${c.code.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                selected
                  ? "bg-orange-600 text-white border-orange-600"
                  : c.active
                    ? "bg-white text-slate-700 border-slate-300 hover:border-orange-400"
                    : "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
              }`}
              title={c.active ? c.hint : `${c.label} — ${c.phase || "coming soon"}`}
            >
              <span className="inline-flex items-center gap-1.5">
                {!c.active && <Lock className="w-3 h-3" />}
                {c.label}
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                    selected
                      ? "bg-white/20 text-white"
                      : c.active
                        ? "bg-slate-100 text-slate-600"
                        : "bg-slate-200 text-slate-500"
                  }`}
                >({seriesLabel})</span>
                {!c.active && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                    {c.phase || "soon"}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div
        className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2"
        data-testid="jv-info-blurb"
      >
        Journal Vouchers are used for accounting adjustments, provisions,
        accruals and other non-operational accounting entries.
      </div>

      <div
        className="text-xs text-slate-500 italic"
        data-testid="jv-category-hint"
      >
        {cat.hint}
      </div>

      {/* Filter bar — Sprint 2026-Feb JV Stabilization */}
      <div
        className="bg-white border border-slate-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
        data-testid="jv-filter-bar"
      >
        <div>
          <label className="text-slate-500 font-medium">Status</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={filters.status}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
            data-testid="jv-filter-status"
          >
            <option value="">All active</option>
            <option value="Draft">Draft</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Superseded">Superseded</option>
          </select>
        </div>
        <div>
          <label className="text-slate-500 font-medium">Branch</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={filters.branch_id}
            onChange={(e) => { setFilters({ ...filters, branch_id: e.target.value }); setPage(1); }}
            data-testid="jv-filter-branch"
          >
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.branch_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-slate-500 font-medium">Ledger</label>
          <SearchableSelect
            value={filters.ledger_id}
            onChange={(id) => { setFilters({ ...filters, ledger_id: id }); setPage(1); }}
            items={usableLedgers}
            getKey={(L) => L.id}
            getLabel={(L) => `${L.code} — ${L.name}`}
            getSubLabel={(L) => L.type || ""}
            getSearchText={(L) => `${L.code || ""} ${L.name || ""} ${L.type || ""}`}
            placeholder="All ledgers"
            searchPlaceholder="Code / name / type…"
            testid="jv-filter-ledger"
          />
        </div>
        <div>
          <label className="text-slate-500 font-medium">Search (Voucher No / Narration)</label>
          <Input
            className="text-xs py-1 h-8"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && setPage(1)}
            placeholder="type & press Enter…"
            data-testid="jv-filter-search"
          />
        </div>
        <div>
          <label className="text-slate-500 font-medium">From Date</label>
          <Input
            type="date"
            className="text-xs py-1 h-8"
            value={filters.from_date}
            onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setPage(1); }}
            data-testid="jv-filter-from-date"
          />
        </div>
        <div>
          <label className="text-slate-500 font-medium">To Date</label>
          <Input
            type="date"
            className="text-xs py-1 h-8"
            value={filters.to_date}
            onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setPage(1); }}
            data-testid="jv-filter-to-date"
          />
        </div>
        <div>
          <label className="text-slate-500 font-medium">Created By</label>
          <Input
            className="text-xs py-1 h-8"
            value={filters.created_by}
            onChange={(e) => setFilters({ ...filters, created_by: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && setPage(1)}
            placeholder="email"
            data-testid="jv-filter-created-by"
          />
        </div>
        <div>
          <label className="text-slate-500 font-medium">Approved By</label>
          <Input
            className="text-xs py-1 h-8"
            value={filters.approved_by}
            onChange={(e) => setFilters({ ...filters, approved_by: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && setPage(1)}
            placeholder="email"
            data-testid="jv-filter-approved-by"
          />
        </div>
        <div className="col-span-2 md:col-span-4 flex justify-end pt-1">
          <button
            className="text-xs text-slate-500 hover:text-slate-800 underline"
            onClick={() => {
              setFilters({
                status: "", branch_id: "", from_date: "", to_date: "",
                ledger_id: "", created_by: "", approved_by: "", search: "",
              });
              setPage(1);
            }}
            data-testid="jv-filter-clear"
          >Clear all filters</button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left">Voucher No</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Branch</th>
              <th className="px-3 py-2 text-left">Narration</th>
              <th className="px-3 py-2 text-right">Debit</th>
              <th className="px-3 py-2 text-right">Credit</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="8" className="p-6 text-center text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="8" className="p-6 text-center text-slate-500" data-testid="jv-empty-state">
                  {cat.active
                    ? `No ${cat.label} JVs match the current filters. Click "New Journal Voucher" or clear filters.`
                    : `${cat.label} is not available yet (${cat.phase || "coming soon"}).`}
                </td>
              </tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`jv-row-${r.id}`}>
                <td className="px-3 py-2 font-mono">{r.voucher_number || "—"}</td>
                <td className="px-3 py-2">{r.voucher_date}</td>
                <td className="px-3 py-2">{r.branch_name || r.branch_id}</td>
                <td className="px-3 py-2 max-w-[280px] truncate" title={r.narration}>
                  {r.narration || "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  ₹{Number(r.total_debit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  ₹{Number(r.total_credit || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-2">
                  <Badge className={_statusColor(r.status)}>{r.status}</Badge>
                  {r.reversal_of && <div className="text-xs text-slate-400 mt-0.5">reversal</div>}
                  {r.reversal_ref && <div className="text-xs text-emerald-600 mt-0.5">reversed</div>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      className="p-1 text-slate-500 hover:text-slate-900"
                      onClick={() => onPdf(r)}
                      title="Download PDF"
                      data-testid={`jv-pdf-${r.id}`}
                    ><FileDown className="w-4 h-4" /></button>
                    {r.status === "Approved" && (
                      <button
                        className="p-1 text-slate-500 hover:text-[#F97316]"
                        onClick={() => setRpEdit(r)}
                        title="Change Project Reporting Period (post-approval, audited)"
                        data-testid={`jv-rp-edit-${r.id}`}
                      ><CalendarClock className="w-4 h-4" /></button>
                    )}
                    {r.status === "Approved" && !r.reversal_ref && !r.reversal_of && (
                      <>
                        <button
                          className="p-1 text-amber-600 hover:text-amber-800"
                          onClick={() => onReverse(r)}
                          title="Reverse this JV (creates a mirror voucher)"
                          data-testid={`jv-reverse-${r.id}`}
                        ><RotateCcw className="w-4 h-4" /></button>
                        <button
                          className="p-1 text-indigo-600 hover:text-indigo-800"
                          onClick={() => onRevise(r)}
                          title="Revise this JV (creates a new /R revision)"
                          data-testid={`jv-revise-${r.id}`}
                        ><History className="w-4 h-4" /></button>
                      </>
                    )}
                    {(r.status === "Draft" || r.status === "Pending") && (
                      <>
                        <button
                          className="px-2 py-0.5 text-xs text-blue-600 hover:underline"
                          onClick={() => onEdit(r)}
                          data-testid={`jv-edit-${r.id}`}
                        >Edit</button>
                        <button
                          className="p-1 text-rose-500 hover:text-rose-700"
                          onClick={() => onDelete(r)}
                          title="Delete"
                          data-testid={`jv-delete-${r.id}`}
                        ><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div
        className="flex items-center justify-between text-xs text-slate-600 pt-1"
        data-testid="jv-pagination"
      >
        <div>
          Showing <span className="font-semibold">{rows.length}</span> of{" "}
          <span className="font-semibold" data-testid="jv-pagination-total">{totalRows}</span> vouchers
          {rows.length ? ` · page ${page} / ${Math.max(1, Math.ceil(totalRows / pageSize))}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-slate-500">Rows per page</label>
          <select
            className="border rounded px-2 py-1"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            data-testid="jv-pagination-size"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            className="p-1 border rounded disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="jv-pagination-prev"
          ><ChevronLeft className="w-4 h-4" /></button>
          <button
            className="p-1 border rounded disabled:opacity-40"
            disabled={page * pageSize >= totalRows}
            onClick={() => setPage((p) => p + 1)}
            data-testid="jv-pagination-next"
          ><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {showForm && editing && (
        <JVForm
          initial={editing}
          category={cat}
          branches={branches}
          projects={projects}
          ledgers={ledgers}
          revisingId={revisingId}
          onClose={() => { setShowForm(false); setEditing(null); setRevisingId(""); }}
          onSaved={async () => { setShowForm(false); setEditing(null); setRevisingId(""); await load(); }}
        />
      )}

      {rpEdit && (
        <ReportingPeriodEditDialog
          resource="journal-vouchers"
          docId={rpEdit.id}
          current={rpEdit.reporting_period || ""}
          voucherLabel={`Journal Voucher ${rpEdit.voucher_number || ""}`}
          onClose={() => setRpEdit(null)}
          onSaved={async () => { setRpEdit(null); await load(); }}
        />
      )}
    </div>
  );
}


function JVForm({ initial, category, branches, projects, ledgers, revisingId, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !revisingId && !!(initial.id && initial.voucher_number);
  const isRevise = !!revisingId;
  const vtype = initial.voucher_type || category.code;

  const totals = useMemo(() => {
    const d = (form.lines || []).reduce((s, l) => s + Number(l.debit || 0), 0);
    const c = (form.lines || []).reduce((s, l) => s + Number(l.credit || 0), 0);
    return { debit: d, credit: c, delta: Math.abs(d - c), balanced: Math.abs(d - c) < 0.01 };
  }, [form.lines]);

  const setLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      lines: (f.lines || []).map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };

  const addLine = () => setForm((f) => ({ ...f, lines: [...(f.lines || []), _emptyLine()] }));
  const rmLine  = (idx) => setForm((f) => ({
    ...f, lines: (f.lines || []).filter((_, i) => i !== idx),
  }));

  const onBranchChange = (id) => {
    const b = branches.find((x) => x.id === id);
    setForm((f) => ({ ...f, branch_id: id, branch_name: b?.branch_name || "" }));
  };

  const onProjectChange = (idx, id) => {
    const p = projects.find((x) => x.id === id);
    setLine(idx, { project_id: id, project_name: p?.project_name || "" });
  };

  const submit = async () => {
    if (!form.branch_id) return toast.error("Branch is required");
    if ((form.lines || []).length < 2) return toast.error("At least 2 lines required");
    for (let i = 0; i < form.lines.length; i++) {
      const l = form.lines[i];
      if (!(l.ledger_id || "").trim()) {
        return toast.error(`Line ${i + 1}: select a ledger from the master`);
      }
      const d = Number(l.debit || 0), c = Number(l.credit || 0);
      if ((d > 0 && c > 0) || (d === 0 && c === 0)) {
        return toast.error(`Line ${i + 1}: exactly one of debit / credit must be > 0`);
      }
    }
    if (!totals.balanced) {
      return toast.error(`Unbalanced — Δ ₹${totals.delta.toFixed(2)}. Fix before saving.`);
    }
    setSaving(true);
    try {
      const payload = {
        voucher_type: vtype,
        voucher_date: form.voucher_date,
        reporting_period: form.reporting_period || "",
        branch_id: form.branch_id,
        branch_name: form.branch_name || "",
        narration: form.narration || "",
        lines: form.lines.map((l, i) => ({
          line_index: i,
          ledger_id: l.ledger_id,
          narration: l.narration || "",
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
          project_id: l.project_id || "",
          project_name: l.project_name || "",
          business_vertical_id: l.business_vertical_id || "",
          cost_centre_id: l.cost_centre_id || "",
        })),
        attachments: form.attachments || [],
      };
      if (isRevise) {
        await api.post(`/journal-vouchers/${revisingId}/revise`, payload);
        toast.success("Revision created & submitted for approval");
      } else if (isEdit) {
        await api.put(`/journal-vouchers/${form.id}`, payload);
        toast.success("JV updated & re-submitted");
      } else {
        await api.post(`/journal-vouchers`, payload);
        toast.success("JV submitted for approval");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="jv-form-modal">
        <DialogHeader>
          <DialogTitle data-testid="jv-form-title">
            {isRevise ? "Revise" : isEdit ? "Edit" : "New"} Journal Voucher · {category.label}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isRevise
              ? "This will supersede the current voucher and create a new revision that must go through approval again. Old versions remain in the audit chain."
              : "Double-entry ledger — enter at least 2 lines where total debits equal total credits."}
            {" "}Category: <span className="font-semibold text-slate-700">{category.label}</span> ({vtype}).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Voucher Date</label>
            <Input
              type="date"
              value={form.voucher_date || ""}
              onChange={(e) => setForm({ ...form, voucher_date: e.target.value })}
              data-testid="jv-date-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Branch</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.branch_id || ""}
              onChange={(e) => onBranchChange(e.target.value)}
              data-testid="jv-branch-select"
            >
              <option value="">— Select branch —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.branch_name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-1">
            <label className="text-xs text-slate-500 font-medium">Voucher No</label>
            <Input value={form.voucher_number || "(auto)"} disabled />
          </div>
          <div className="col-span-1">
            <label className="text-xs text-slate-500 font-medium">Project Reporting Period <span className="text-slate-400">(optional)</span></label>
            <Input
              type="month"
              value={form.reporting_period || ""}
              onChange={(e) => setForm({ ...form, reporting_period: e.target.value })}
              data-testid="jv-reporting-period"
            />
            <RpNote />
          </div>
          <div className="col-span-3">
            <label className="text-xs text-slate-500 font-medium">Narration</label>
            <Textarea
              rows={2}
              value={form.narration || ""}
              onChange={(e) => setForm({ ...form, narration: e.target.value })}
              placeholder="e.g. April 2026 bonus provision — internal staff"
              data-testid="jv-narration-input"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold text-slate-800">Line Items</div>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="jv-add-line">
              <Plus className="w-3 h-3 mr-1" /> Add Line
            </Button>
          </div>
          <div className="border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-2 py-1 text-left w-8">#</th>
                  <th className="px-2 py-1 text-left">Ledger</th>
                  <th className="px-2 py-1 text-left">Narration</th>
                  <th className="px-2 py-1 text-left">Project (opt)</th>
                  <th className="px-2 py-1 text-right w-28">Debit</th>
                  <th className="px-2 py-1 text-right w-28">Credit</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(form.lines || []).map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                    <td className="px-1 py-1">
                      <SearchableSelect
                        value={l.ledger_id || ""}
                        onChange={(id, row) => {
                          setLine(i, {
                            ledger_id: id,
                            ledger_code: row?.code || "",
                            ledger_name: row?.name || "",
                            ledger_type: row?.type || "",
                            account_label: row?.name || "",
                          });
                        }}
                        items={(ledgers || [])
                          .filter((L) => L.active)
                          .filter((L) => {
                            const apply = L.branch_applicability || [];
                            if (!apply.length) return true;
                            return !form.branch_id || apply.includes(form.branch_id);
                          })}
                        getKey={(L) => L.id}
                        getLabel={(L) => `${L.code} — ${L.name}`}
                        getSubLabel={(L) => L.type || ""}
                        getSearchText={(L) => `${L.code || ""} ${L.name || ""} ${L.type || ""}`}
                        placeholder="— Select ledger —"
                        searchPlaceholder="Code / name / type…"
                        testid={`jv-line-${i}-ledger`}
                        inputClassName="text-sm"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        value={l.narration || ""}
                        onChange={(e) => setLine(i, { narration: e.target.value })}
                        placeholder="optional"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <SearchableSelect
                        value={l.project_id || ""}
                        onChange={(id) => onProjectChange(i, id)}
                        items={projects}
                        getKey={(p) => p.id}
                        getLabel={(p) => `${p.project_code || ""} — ${p.project_name || ""}`}
                        getSearchText={(p) => `${p.project_code || ""} ${p.project_name || ""}`}
                        placeholder="— Common overhead —"
                        searchPlaceholder="Project code / name…"
                        emptyText="No projects"
                        testid={`jv-line-${i}-project`}
                        inputClassName="text-sm"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="text-right"
                        value={l.debit || 0}
                        onChange={(e) => setLine(i, {
                          debit: Number(e.target.value) || 0,
                          credit: Number(e.target.value) > 0 ? 0 : l.credit,
                        })}
                        data-testid={`jv-line-${i}-debit`}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="text-right"
                        value={l.credit || 0}
                        onChange={(e) => setLine(i, {
                          credit: Number(e.target.value) || 0,
                          debit: Number(e.target.value) > 0 ? 0 : l.debit,
                        })}
                        data-testid={`jv-line-${i}-credit`}
                      />
                    </td>
                    <td className="px-1 py-1 text-center">
                      {(form.lines || []).length > 2 && (
                        <button
                          className="text-rose-500 hover:text-rose-700"
                          onClick={() => rmLine(i)}
                          data-testid={`jv-line-${i}-remove`}
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="border-t bg-slate-50 font-bold">
                  <td colSpan="4" className="px-2 py-2 text-right">TOTAL</td>
                  <td className="px-2 py-2 text-right font-mono" data-testid="jv-total-debit">
                    ₹{totals.debit.toFixed(2)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono" data-testid="jv-total-credit">
                    ₹{totals.credit.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            className={`mt-2 text-sm font-medium px-3 py-2 rounded ${
              totals.balanced
                ? "bg-emerald-50 text-emerald-700"
                : "bg-rose-50 text-rose-700"
            }`}
            data-testid="jv-balance-banner"
          >
            {totals.balanced
              ? `Balanced ✓ (Σ debit = Σ credit = ₹${totals.debit.toFixed(2)})`
              : `Unbalanced ✗ — Δ ₹${totals.delta.toFixed(2)}. Fix before saving.`}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !totals.balanced}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="jv-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {isEdit ? "Update & Re-submit" : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
